/**
 * Formatting utilities for dates, times, prices, and text
 */

/**
 * Format a time for display (HH:MM format)
 * @param {Date} date - Date object
 * @returns {string} Formatted time string
 */
export function formatTime(date) {
    if (!(date instanceof Date) || isNaN(date)) {
        return '';
    }
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Format a date for display
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Formatted date string
 */
export function formatDate(dateStr) {
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch (e) {
        return dateStr;
    }
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Relative time string
 */
export function formatRelativeTime(dateStr) {
    try {
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        const hours = Math.floor(diff / (1000 * 60 * 60));

        if (hours < 1) return 'Just now';
        if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

        const days = Math.floor(hours / 24);
        return `${days} day${days > 1 ? 's' : ''} ago`;
    } catch (e) {
        return dateStr;
    }
}

/**
 * Format a date with "today", "yesterday", or relative days
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Formatted date string
 */
export function formatDateRelative(dateStr) {
    try {
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'today';
        if (diffDays === 1) return 'yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch (e) {
        return dateStr;
    }
}

/**
 * Format price with currency symbol
 * @param {number|string} price - Price value
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted price string
 */
export function formatPrice(price, currency = 'USD') {
    const num = parseFloat(price) || 0;
    const symbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        HKD: 'HK$',
        CAD: 'C$',
        AUD: 'A$',
        JPY: '¥',
        CNY: '¥',
        INR: '₹',
        SGD: 'S$',
        NZD: 'NZ$'
    };
    const symbol = symbols[currency] || currency + ' ';

    // JPY and some other currencies don't use decimal places
    const noDecimals = ['JPY', 'KRW', 'VND'];
    if (noDecimals.includes(currency)) {
        return `${symbol}${Math.round(num).toLocaleString()}`;
    }

    return `${symbol}${num.toFixed(2)}`;
}

/**
 * Format order/ticket status for display
 * @param {string} status - Status code
 * @returns {string} Human-readable status
 */
export function formatStatus(status) {
    const labels = {
        'pending': 'Pending',
        'confirmed': 'Confirmed',
        'processing': 'Processing',
        'shipped': 'Shipped',
        'out_for_delivery': 'Out for Delivery',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled',
        'refunded': 'Refunded',
        'on_hold': 'On Hold',
        'open': 'Open',
        'closed': 'Closed',
        'resolved': 'Resolved'
    };
    return labels[status] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

/**
 * Format message text with basic markdown-like syntax
 * Converts **bold**, *italic*, and \n to HTML
 * @param {string} text - Raw message text
 * @returns {string} HTML-formatted text
 */
export function formatMessageText(text) {
    if (!text) return '';
    return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>');
}

/**
 * Truncate text to a maximum length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export function truncateText(text, maxLength = 100) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
}

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
export function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Calculate discount percentage
 * @param {number} originalPrice - Original price
 * @param {number} currentPrice - Current/sale price
 * @returns {number} Discount percentage (0-100)
 */
export function calculateDiscountPercent(originalPrice, currentPrice) {
    if (!originalPrice || originalPrice <= currentPrice) return 0;
    return Math.round((1 - currentPrice / originalPrice) * 100);
}
